import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import { CheckCircleFill, Eye, EyeSlash, XLg } from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';
import { getStoredAuthToken, getStoredAuthUser } from '../utils/authStorage';
import './auth-shared.css';

const AUTH_NOTICE_MESSAGE = 'authNoticeMessage';

/* ===========================================================
   Login Page Component
   Allows existing users to sign in to their AcceliQA account.
   Renders as a popup overlay on top of the Landing page,
   sharing the same design language as the Register page.
   =========================================================== */

/* ---------- Type definitions ---------- */

/** Shape of the login form state */
interface LoginFormData
{
    email: string;
    password: string;
}

/** Per-field validation error messages (null means valid) */
interface LoginFieldErrors
{
    email: string | null;
    password: string | null;
}

/* ---------- Helper / validation functions ---------- */

/**
 * Validates each login field individually and returns per-field error messages.
 * A null value for a field means that field is valid.
 */
function validateLoginFields(formData: LoginFormData): LoginFieldErrors
{
    const errors: LoginFieldErrors = {
        email: null,
        password: null,
    };

    /* Email must not be empty and should contain @ */
    if (!formData.email.trim())
    {
        errors.email = 'Email is required';
    }
    else if (!/\S+@\S+\.\S+/.test(formData.email))
    {
        errors.email = 'Please enter a valid email address';
    }

    /* Password must not be empty */
    if (!formData.password)
    {
        errors.password = 'Password is required';
    }

    return errors;
}

/**
 * Returns true when all field errors are null (i.e. no validation issues).
 */
function isLoginFormValid(errors: LoginFieldErrors): boolean
{
    return Object.values(errors).every((val) => val === null);
}

/* ---------- Main component ---------- */

const LoginNew: React.FC = () =>
{
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    /* -- MCP browser-login flow: detect redirect params -- */
    const _searchParams = new URLSearchParams(location.search);
    const mcpRedirect = _searchParams.get('mcp_redirect');
    const mcpState = _searchParams.get('mcp_state');
    // Only allow localhost / 127.0.0.1 callbacks (security: prevent open redirect)
    const isMcpFlow = !!(mcpRedirect && /^http:\/\/(127\.0\.0\.1|localhost):\d+\//.test(mcpRedirect));
    const [authNotice] = useState<string | null>(() => {
        const fromState = (location.state as { authNotice?: string } | undefined)?.authNotice || null;
        const fromSession = sessionStorage.getItem(AUTH_NOTICE_MESSAGE);
        return fromState || fromSession;
    });

    /* -- Form state -- */
    const [formData, setFormData] = useState<LoginFormData>({
        email: '',
        password: '',
    });

    /* -- UI state -- */
    const [loading, setLoading] = useState<boolean>(false);
    const [serverError, setServerError] = useState<string | null>(null);

    /* -- Tracks whether the user has attempted to submit at least once -- */
    const [submitted, setSubmitted] = useState<boolean>(false);

    /* -- Password visibility toggle -- */
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [rememberMe, setRememberMe] = useState<boolean>(false);

    /* -- Compute field-level errors (recalculated every render) -- */
    const fieldErrors: LoginFieldErrors = validateLoginFields(formData);

    useEffect(() =>
    {
        if (authNotice)
        {
            sessionStorage.removeItem(AUTH_NOTICE_MESSAGE);
        }
    }, [authNotice]);

    /* ---------- Event handlers ---------- */

    /**
     * Handles input field changes and updates form state.
     * Also clears any existing server error message on each keystroke.
     */
    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>): void
    {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        /* Clear server error when user starts typing again */
        if (serverError)
        {
            setServerError(null);
        }

    }

    /**
     * Toggles the visibility of the password field.
     */
    function handleTogglePasswordVisibility(): void
    {
        setShowPassword((prev) => !prev);
    }

    function handleRememberMeChange(e: React.ChangeEvent<HTMLInputElement>): void
    {
        setRememberMe(e.target.checked);
    }

    /**
     * Handles form submission.
     * Validates, calls the login API, and navigates on success.
     */
    async function handleFormSubmit(e: React.FormEvent): Promise<void>
    {
        e.preventDefault();

        /* Flag that the user attempted submission so field errors render */
        setSubmitted(true);

        /* Run per-field validation */
        const errors = validateLoginFields(formData);
        if (!isLoginFormValid(errors))
        {
            return;
        }

        setLoading(true);
        setServerError(null);

        try
        {
            const res = await login(formData.email, formData.password, rememberMe);

            if (res?.mfa_required)
            {
                navigate('/mfa', {
                    replace: true,
                    state: {
                        from: (location.state as any)?.from,
                        ...(isMcpFlow ? { mcpRedirect, mcpState } : {}),
                    },
                });
                return;
            }

            /* If the user must change their password, redirect to set-password */
            if (res?.must_change_password)
            {
                navigate('/set-password', { replace: true });
                return;
            }

            /* MCP browser-login flow: redirect to local callback with token */
            if (isMcpFlow && mcpRedirect && mcpState)
            {
                const token = getStoredAuthToken() || '';
                let orgId = '';
                try
                {
                    const userObj = getStoredAuthUser<{ active_organization_id?: number | string; organization_id?: number | string }>() || {};
                    orgId = String(userObj.active_organization_id ?? userObj.organization_id ?? '');
                }
                catch { /* ignore */ }

                const callbackUrl = new URL(mcpRedirect);
                callbackUrl.searchParams.set('token', token);
                callbackUrl.searchParams.set('state', mcpState);
                callbackUrl.searchParams.set('org_id', orgId);
                callbackUrl.searchParams.set('email', formData.email.trim());
                window.location.href = callbackUrl.toString();
                return;
            }

            /* Redirect to the page they were trying to access, or dashboard.
               Never route back to /set-password once password-change is no longer required. */
            const requestedFrom = (location.state as any)?.from?.pathname;
            const blockedFrom = new Set(['/set-password']);
            const from = requestedFrom && !blockedFrom.has(requestedFrom)
                ? requestedFrom
                : '/dashboard';
            navigate(from, { replace: true });
        }
        catch (err: any)
        {
            const message =
                err.response?.data?.detail ||
                'Login failed. Please check your credentials.';
            setServerError(message);
        }
        finally
        {
            setLoading(false);
        }
    }

    function handleClose(): void
    {
        navigate('/');
    }

    /* ---------- Main render ---------- */

    return (
        <div className="auth-page">
            <div className="auth-card">
                <button
                    type="button"
                    className="auth-close-btn"
                    onClick={handleClose}
                    aria-label="Close login popup"
                >
                    <XLg size={18} />
                </button>

                {/* Header section: logo, heading, subtitle */}
                <div className="text-center">
                    <img
                        src="/Accelirate.svg"
                        alt="AcceliQA Logo"
                        className="auth-logo"
                    />
                    <h2 className="auth-heading">Welcome Back</h2>
                    <p className="auth-subtitle">
                        Sign in to your AcceliQA account
                    </p>
                </div>

                {/* MCP authorization banner */}
                {isMcpFlow && (
                    <Alert variant="info" className="mb-3 text-center" style={{ fontSize: '0.875rem' }}>
                        <strong>acceliqa-mcp</strong> is requesting access to your account.
                        Sign in below to authorise the connection.
                    </Alert>
                )}

                {/* Server error message (e.g. wrong credentials) */}
                {authNotice && (
                    <Alert variant="success" className="mb-3">
                        {authNotice}
                    </Alert>
                )}

                {serverError && (
                    <div className="auth-server-error">
                        {serverError}
                    </div>
                )}

                {/* Login form — noValidate disables browser validation so our custom red borders show */}
                <Form onSubmit={handleFormSubmit} noValidate>

                    {/* Email field */}
                    <div className="auth-form-group">
                        <Form.Label className="auth-label">
                            Your Email
                        </Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Enter your email"
                            required
                            isInvalid={submitted && !!fieldErrors.email}
                        />

                        {/* Per-field validation feedback */}
                        {submitted && fieldErrors.email && (
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.email}
                            </Form.Control.Feedback>
                        )}
                    </div>

                    {/* Password field with visibility toggle */}
                    <div className="auth-form-group">
                        <Form.Label className="auth-label">
                            Password
                        </Form.Label>

                        {/* Input + toggle wrapper */}
                        <div className="password-input-wrapper">
                            <Form.Control
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Enter your password"
                                required
                                className="password-input-field"
                                isInvalid={submitted && !!fieldErrors.password}
                            />

                            {/* Visibility toggle button */}
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={handleTogglePasswordVisibility}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                            </button>
                        </div>

                        {/* Per-field validation feedback (outside wrapper so toggle stays aligned) */}
                        {submitted && fieldErrors.password && (
                            <div className="field-error-text">
                                {fieldErrors.password}
                            </div>
                        )}
                    </div>

                    {/* Remember me checkbox */}
                    {/* Remember me + Forgot Password row */}
                    <div className="auth-remember-forgot-row">
                        <div className="auth-checkbox-row auth-checkbox-row-compact">
                            <input
                                type="checkbox"
                                id="remember-me-checkbox"
                                className="remember-me-checkbox-input"
                                checked={rememberMe}
                                onChange={handleRememberMeChange}
                            />
                            <label htmlFor="remember-me-checkbox" className="auth-checkbox-visual-label">
                                <span className="remember-me-indicator" aria-hidden="true">
                                    {rememberMe
                                        ? <CheckCircleFill size={18} className="remember-me-indicator-check" />
                                        : <span className="remember-me-indicator-circle" />}
                                </span>
                                <span className="auth-checkbox-label">Remember me</span>
                            </label>
                        </div>
                        <Link to="/forgot-password" className="auth-footer-link auth-forgot-link">
                            Forgot Password?
                        </Link>
                    </div>

                    {/* Submit button */}
                    <button
                        type="submit"
                        className="auth-submit-btn"
                        disabled={loading}
                    >
                        {loading
                            ? <Spinner as="span" animation="border" size="sm" />
                            : 'Sign In'
                        }
                    </button>

                    {/* Footer link — redirect to register */}
                    <p className="auth-footer-text">
                        Don't have an account?
                        <Link to="/early-access" className="auth-footer-link">
                            Sign Up Here
                        </Link>
                    </p>

                </Form>
            </div>
        </div>
    );
};

export default LoginNew;
